/**
 * Page-load fast path for slide thumbnails.
 *
 * A list view used to fan out N parallel `/api/slide-sense/slides/<id>/thumbnail`
 * requests. Even with a per-worker LRU cache on the backend, EasyAuth +
 * App Service ingress run on every request and serialize across worker
 * queues, so 80 visible slides took >5s wall. This hook calls the batch
 * endpoint once per slide list, decodes each PNG into an object URL,
 * and stores it in a Zustand map. `useSlideThumbnail` consults that map
 * before falling back to the per-slide URL.
 *
 * The map is keyed by slide id; entries persist across re-renders and
 * route changes. Object URLs are intentionally not revoked — they're
 * cheap and the user's session lifetime is bounded.
 */

import { useEffect, useRef } from "react"

import { create } from "zustand"

import { fetchThumbnailsBatch } from "../api"

const BATCH_SIZE = 100

interface ThumbnailCacheState {
  urls: Map<string, string>
  inFlight: Set<string>
  /** Ids the backend has reported as unavailable (missing, render_failed,
   *  tenant mismatch). Lets the hook surface broken-image without spinning
   *  forever on retries. */
  failed: Set<string>
  setUrl: (slideId: string, url: string) => void
  setUrls: (entries: Array<[string, string]>) => void
  markInFlight: (slideIds: string[]) => void
  clearInFlight: (slideIds: string[]) => void
  markFailed: (slideIds: string[]) => void
  has: (slideId: string) => boolean
  get: (slideId: string) => string | undefined
  isPending: (slideId: string) => boolean
  hasFailed: (slideId: string) => boolean
}

export const useThumbnailCache = create<ThumbnailCacheState>((set, get) => ({
  urls: new Map(),
  inFlight: new Set(),
  failed: new Set(),
  setUrl: (slideId, url) =>
    set((state) => {
      const next = new Map(state.urls)
      next.set(slideId, url)
      return { urls: next }
    }),
  setUrls: (entries) =>
    set((state) => {
      if (entries.length === 0) return state
      const next = new Map(state.urls)
      for (const [id, url] of entries) next.set(id, url)
      return { urls: next }
    }),
  markInFlight: (slideIds) =>
    set((state) => {
      if (slideIds.length === 0) return state
      const next = new Set(state.inFlight)
      for (const id of slideIds) next.add(id)
      return { inFlight: next }
    }),
  clearInFlight: (slideIds) =>
    set((state) => {
      if (slideIds.length === 0) return state
      const next = new Set(state.inFlight)
      for (const id of slideIds) next.delete(id)
      return { inFlight: next }
    }),
  markFailed: (slideIds) =>
    set((state) => {
      if (slideIds.length === 0) return state
      const next = new Set(state.failed)
      for (const id of slideIds) next.add(id)
      return { failed: next }
    }),
  has: (slideId) => get().urls.has(slideId),
  get: (slideId) => get().urls.get(slideId),
  isPending: (slideId) => get().inFlight.has(slideId),
  hasFailed: (slideId) => get().failed.has(slideId),
}))

/** Drop a cached thumbnail blob URL (e.g. after in-place reprocess). */
export function evictSlideThumbnailCache(slideId: string): void {
  const cache = useThumbnailCache.getState()
  const existing = cache.urls.get(slideId)
  if (existing) {
    URL.revokeObjectURL(existing)
  }
  useThumbnailCache.setState((state) => {
    const urls = new Map(state.urls)
    urls.delete(slideId)
    const inFlight = new Set(state.inFlight)
    inFlight.delete(slideId)
    const failed = new Set(state.failed)
    failed.delete(slideId)
    return { urls, inFlight, failed }
  })
}

function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: contentType })
}

export async function prefetchSlideThumbnails(slideIds: string[]): Promise<void> {
  if (slideIds.length === 0) return
  const cache = useThumbnailCache.getState()
  const missing = slideIds.filter((id) => !cache.has(id) && !cache.isPending(id))
  if (missing.length === 0) return

  cache.markInFlight(missing)

  const chunks: string[][] = []
  for (let i = 0; i < missing.length; i += BATCH_SIZE) {
    chunks.push(missing.slice(i, i + BATCH_SIZE))
  }

  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const response = await fetchThumbnailsBatch(chunk)
        const entries: Array<[string, string]> = []
        for (const t of response.thumbnails) {
          const blob = base64ToBlob(t.data_b64, t.content_type)
          entries.push([t.slide_id, URL.createObjectURL(blob)])
        }
        useThumbnailCache.getState().setUrls(entries)
        if (response.missing.length > 0) {
          useThumbnailCache.getState().markFailed(response.missing)
        }
      } catch (err) {
        // Mark the chunk failed even on transient HTTP errors. An earlier
        // pass tried "transient → clear inFlight only" so the next render
        // could retry — but useSlideThumbnail's effect re-queues whenever
        // isPending flips false, which created a tight retry loop while
        // the endpoint was unhealthy (every clearInFlight triggered a
        // re-render → re-effect → re-request). The 50ms debounce limits
        // throughput per slide but doesn't help across many slides.
        //
        // Trade-off: a transient outage now sticks thumbnails as broken
        // until page reload. That's acceptable — thumbnails are
        // non-critical UX, and the alternative DOSes the backend.
        // Proper fix (per-id backoff/cooldown) is tracked separately.
        if (typeof console !== "undefined") {
          // eslint-disable-next-line no-console
          console.warn("Thumbnail batch fetch failed:", err)
        }
        useThumbnailCache.getState().markFailed(chunk)
      } finally {
        useThumbnailCache.getState().clearInFlight(chunk)
      }
    })
  )
}

/**
 * Run once when the slide list changes. Skips slide ids already in
 * cache, so re-renders and pagination append-only updates only fetch
 * what's new.
 */
export function useThumbnailPrefetch(slides: ReadonlyArray<{ id: string }> | undefined): void {
  const seen = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!slides || slides.length === 0) return
    const cache = useThumbnailCache.getState()
    const fresh: string[] = []
    for (const s of slides) {
      if (!s.id) continue
      if (seen.current.has(s.id)) continue
      if (cache.has(s.id)) {
        seen.current.add(s.id)
        continue
      }
      fresh.push(s.id)
      seen.current.add(s.id)
    }
    if (fresh.length > 0) {
      void prefetchSlideThumbnails(fresh)
    }
  }, [slides])
}
